import { useEffect, useState } from 'react';

import { ocppMessageType } from '../../config/enum';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchAllCharingPoints } from '../../redux/slices/ChargingPointSlice';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import MyButton from '../../shared-components/button/Button';
import CustomInput from '../../shared-components/custom-input/CustomInput';
import ModalBox from '../../shared-components/modal-box/ModalBox';
import MultiSelectDropdown from '../../shared-components/multi-select-field/MultiSelectField';
import PageTitle from '../../shared-components/page-title/PageTitle';
import SuccessNotification from '../../shared-components/success-notification/SuccessNotification';
import ocppEvents from '../../utils/api/ocpp-events';

interface TriggerMessageProperties {
  devices: string[];
  connectorId: string;
  requestedMessage: string;
}

interface ModalBoxDetail {
  title: string;
  content: JSX.Element;
  notificationModal: boolean;
  rightCorner: string;
}

/**
 * OCPP message command change configuration
 */
function TriggerMessage() {
  const dispatch = useAppDispatch();
  const { chargingPoints } = useAppSelector((state) => state.chargingPoint);

  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: <div />,
    rightCorner: '',
    notificationModal: false,
  });

  const [configuration, setConfiguration] = useState<TriggerMessageProperties>({
    devices: [],
    connectorId: '',
    requestedMessage: '',
  });

  /**
   * Handle the input change function
   * @param {any} name - The name
   * @param {any} value - The value
   */
  const handleInputChange = (name: string, value: string | number | (string | number)[]) => {
    setConfiguration((previousValue) => ({
      ...previousValue,
      [name]: value,
    }));
  };

  /**
   * handle select all
   */
  const handleSelectAll = () => {
    setConfiguration((previousValue) => ({
      ...previousValue,
      devices: chargingPoints.map((device) => device.value) || [],
    }));
  };

  /**
   * handle select none
   */
  const handleSelectNone = () => {
    setConfiguration((previousValue) => ({
      ...previousValue,
      devices: [],
    }));
  };

  /**
   * Function to reset configuration input
   */
  const handleResetInput = () => {
    setModalBoxStatus('close');
    setConfiguration({
      devices: [],
      connectorId: '',
      requestedMessage: '',
    });
  };

  /**
   * Function to submit configuration
   */
  const handleSubmit = async () => {
    if (
      configuration.requestedMessage &&
      configuration.connectorId &&
      configuration.devices.length > 0
    ) {
      ocppEvents
        .anyCommand({
          type: ocppMessageType.TRIGGER_MESSAGE,
          body: {
            connectorId: configuration.connectorId,
            requestedMessage: configuration.requestedMessage,
          },
          chargingPointSns: configuration.devices,
        })
        .then(() => {
          setModalBoxStatus('open');
          setModalBoxDetail({
            title: '',
            rightCorner: '',
            content: (
              <SuccessNotification
                title="Configuration Change Requested "
                description=""
                notificationCloseTimer={3000}
                onNotificationClose={handleResetInput}
              />
            ),
            notificationModal: true,
          });
        })
        .catch((error) =>
          dispatch(
            setNotificationMessage({
              status: true,
              message: error?.response?.data?.message || 'Something went wrong!',
              type: 'error',
              code: 0,
            }),
          ),
        );
    } else {
      dispatch(
        setNotificationMessage({
          status: true,
          message: 'Key, value & device required!',
          type: 'error',
          code: 0,
        }),
      );
    }
  };

  useEffect(() => {
    if (chargingPoints.length === 0) {
      dispatch(fetchAllCharingPoints());
    }
  }, []);

  return (
    <div className="change-configuration-wrap">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        rightCorner={modalBoxDetail.rightCorner}
        notification={modalBoxDetail.notificationModal}
        header={!modalBoxDetail.notificationModal}
      />
      <PageTitle title="Charge Points with OCPP v1.6 (Trigger message)" />
      <div className="change-configuration-container">
        <div className="configuration-multi-select-wrap">
          <MultiSelectDropdown
            placeholderFilled="Devices"
            title="Select Device"
            options={chargingPoints}
            name="devices"
            placeholder="Select Device"
            search
            selectedValues={configuration.devices}
            onSelectionChange={handleInputChange}
          />
          <div className="multiple-button">
            <MyButton label="Select All" buttonType="submit" onClickFunc={handleSelectAll} />
            <MyButton label="Select None" buttonType="submit" onClickFunc={handleSelectNone} />
          </div>
        </div>
        <div className="side-heading">
          <p>Parameters</p>
        </div>
        <div className="change-configuration-parameters">
          <div className="single-row">
            <CustomInput
              name="connectorId"
              inputLabel="Connector Id"
              type="text"
              placeholder="Enter Connector Id"
              value={configuration.connectorId}
              onChangeValue={handleInputChange}
            />
            <CustomInput
              name="requestedMessage"
              inputLabel="Request Message"
              type="text"
              placeholder="Enter request message"
              value={configuration.requestedMessage}
              onChangeValue={handleInputChange}
            />
          </div>
        </div>
        <div className="form-buttons">
          <MyButton label="Cancel" buttonType="back" onClickFunc={handleResetInput} />
          <MyButton label="Submit" buttonType="submit" onClickFunc={handleSubmit} />
        </div>
      </div>
    </div>
  );
}

export default TriggerMessage;
